/**
 * Image Utilities - Handles dynamic image path generation
 * Supports the folder structure: photos/cards/0/, photos/cards/1/, etc.
 * Each folder contains images in ranges: 0 (1-999), 1 (1000-1999), 2 (2000-2999), etc.
 */

import { getImagesVersion } from './cache/data-cache.js';


/**
 * Calculate the correct folder number based on image ID
 * @param {number} imageId - The image ID
 * @returns {number} - The folder number (0, 1, 2, etc.)
 */
export function getImageFolder(imageId) {
    return Math.floor(imageId / 100); // 100-based: 0-99→0, 100-199→1, etc.
}

/**
 * Add version query parameter to image URL for cache-busting
 * @param {string} url - The image URL
 * @returns {string} - Versioned URL
 */
function addImageVersion(url) {
    const version = getImagesVersion();
    return `${url}?v=${version}`;
}

/**
 * Generate the complete image path with fallback support
 * @param {number} imageId - The image ID
 * @param {string} type - The image type ('cards' or 'blog')
 * @param {string} format - Preferred format ('webp' or 'jpg')
 * @returns {Object} - Object with webp and jpg paths (versioned)
 */
export function getImagePaths(imageId, type = 'cards', format = 'webp') {
    const folder = getImageFolder(imageId);
    const basePath = `/images/photos/${type}/${folder}/${imageId}`;
    const paths = {
        webp: addImageVersion(`${basePath}.webp`),
        jpg: addImageVersion(`${basePath}.jpg`),
        preferred: format === 'webp' ? addImageVersion(`${basePath}.webp`) : addImageVersion(`${basePath}.jpg`)
    };
    return paths;
}

/**
 * Get the best available image path with format detection
 * @param {number} imageId - The image ID
 * @param {string} type - The image type ('cards' or 'blog')
 * @param {string} preferredFormat - Preferred format ('webp' or 'jpg')
 * @returns {string} - The best available image path
 */
export function getBestImagePath(imageId, type = 'cards', preferredFormat = 'webp') {
    const paths = getImagePaths(imageId, type, preferredFormat);

    // For now, return the preferred format
    // In the future, we could add format detection logic here
    return paths.preferred;
}

/**
 * Generate image paths for multiple images
 * @param {Array} imageIds - Array of image IDs
 * @param {string} type - The image type ('cards' or 'blog')
 * @param {string} format - Preferred format ('webp' or 'jpg')
 * @returns {Array} - Array of image path objects
 */
export function getMultipleImagePaths(imageIds, type = 'cards', format = 'webp') {
    return imageIds.map(id => getImagePaths(id, type, format));
}

/**
 * Get image paths for cards (default type)
 * @param {number} imageId - The image ID
 * @param {string} format - Preferred format ('webp' or 'jpg')
 * @returns {Object} - Object with webp and jpg paths
 */
export function getCardImagePaths(imageId, format = 'webp') {
    return getImagePaths(imageId, 'cards', format);
}

/**
 * Get image paths for blog images
 * @param {number} imageId - The image ID
 * @param {string} format - Preferred format ('webp' or 'jpg')
 * @returns {Object} - Object with webp and jpg paths
 */
export function getBlogImagePaths(imageId, format = 'webp') {
    return getImagePaths(imageId, 'blog', format);
} 